import {
  Close,
  Download,
  Image as ImageIcon,
  InsertDriveFile,
  VideoFile,
} from "@mui/icons-material";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  Fade,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ticketsAPI } from "../services/api";
import { resolveAttachmentUrl } from "../utils/urlUtils";

const AttachmentPreview = ({ attachments = [], allowDownload = true }) => {
  const { t, i18n } = useTranslation();
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const isImage = (filename) => {
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".bmp",
      ".webp",
      ".svg",
    ];
    return imageExtensions.some((ext) => filename.toLowerCase().endsWith(ext));
  };

  const isVideo = (filename) => {
    const videoExtensions = [
      ".mp4",
      ".avi",
      ".mov",
      ".wmv",
      ".flv",
      ".webm",
      ".mkv",
    ];
    return videoExtensions.some((ext) => filename.toLowerCase().endsWith(ext));
  };

  const getFileIcon = (filename) => {
    if (isImage(filename)) return <ImageIcon />;
    if (isVideo(filename)) return <VideoFile />;
    return <InsertDriveFile />;
  };

  const handleAttachmentClick = (attachment) => {
    setSelectedAttachment(attachment);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAttachment(null);
  };

  const handleDownload = async (attachment) => {
    try {
      if (attachment?.attachment_id) {
        await ticketsAPI.downloadAttachment(attachment);
        return;
      }
    } catch {
      // Fall back to the media URL if the download endpoint is unavailable.
    }
    const link = document.createElement("a");
    link.href = resolveAttachmentUrl(attachment);
    link.download = attachment.filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderAttachmentThumbnail = (attachment, index) => {
    const isImg = isImage(attachment.filename);
    const isVid = isVideo(attachment.filename);
    const fileUrl = resolveAttachmentUrl(attachment);

    return (
      <Card
        key={index}
        sx={{
          width: 200,
          height: 200,
          margin: 1,
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            transform: "scale(1.02)",
            transition: "transform 0.2s ease-in-out",
            boxShadow: 3,
          },
        }}
        onClick={() => handleAttachmentClick(attachment)}
      >
        {isImg ? (
          <CardMedia
            component="img"
            height="100%"
            image={fileUrl}
            alt={attachment.filename}
            sx={{
              objectFit: "cover",
              width: "100%",
              height: "100%",
            }}
          />
        ) : isVid ? (
          <Box sx={{ position: "relative", height: "100%" }}>
            <video
              src={fileUrl}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              muted
              preload="metadata"
            />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                borderRadius: "50%",
                width: 60,
                height: 60,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VideoFile sx={{ color: "white", fontSize: 30 }} />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "grey.100",
            }}
          >
            {getFileIcon(attachment.filename)}
            <Typography
              variant="caption"
              sx={{ mt: 1, textAlign: "center", px: 1 }}
            >
              {attachment.filename}
            </Typography>
          </Box>
        )}

        {/* Download button overlay */}
        {allowDownload && (
          <Tooltip title={t("common.download")}>
            <IconButton
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.9)",
                },
              }}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(attachment);
              }}
            >
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* File type indicator */}
        <Chip
          label={
            isImg
              ? t("common.image")
              : isVid
              ? t("common.video")
              : t("common.file")
          }
          size="small"
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "white",
          }}
        />
      </Card>
    );
  };

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: "bold",
          }}
        >
          {t("common.attachments")} ({attachments.length})
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {attachments.map((attachment, index) =>
            renderAttachmentThumbnail(attachment, index)
          )}
        </Box>
      </Box>

      {/* Modal for full-size viewing */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              outline: "none",
            }}
          >
            {/* Close button */}
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                top: -50,
                right: 0,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                zIndex: 1,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.9)",
                },
              }}
            >
              <Close />
            </IconButton>

            {/* Download button */}
            {allowDownload && (
              <IconButton
                onClick={() => handleDownload(selectedAttachment)}
                sx={{
                  position: "absolute",
                  top: -50,
                  right: 50,
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  zIndex: 1,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                }}
              >
                <Download />
              </IconButton>
            )}

            {selectedAttachment && (
              <Box>
                {isImage(selectedAttachment.filename) ? (
                  <img
                    src={resolveAttachmentUrl(selectedAttachment)}
                    alt={selectedAttachment.filename}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: 8,
                    }}
                  />
                ) : isVideo(selectedAttachment.filename) ? (
                  <video
                    src={resolveAttachmentUrl(selectedAttachment)}
                    controls
                    autoPlay
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      borderRadius: 8,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      backgroundColor: "grey.100",
                      p: 4,
                      borderRadius: 2,
                      textAlign: "center",
                      minWidth: 300,
                      minHeight: 200,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {getFileIcon(selectedAttachment.filename)}
                    <Typography variant="h6" sx={{ mt: 2 }}>
                      {selectedAttachment.filename}
                    </Typography>
                    {allowDownload && (
                      <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={() => handleDownload(selectedAttachment)}
                        sx={{ mt: 2 }}
                      >
                        {t("common.download")}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default AttachmentPreview;
